import {AfterViewInit, Component, isDevMode, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {environment} from "../../../../environments/environment";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {MatTableDataSource} from "@angular/material/table";
import {Centre} from "../../../interfaces/Centres";
import {CentreService} from "../../../services/centre.service";
import {Observable, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {MatPaginator} from "@angular/material/paginator";
import {FinancialYearService} from "../../../services/financial-year.service";

export interface DailyInventoryCount {
  centreCode: string;
  createdAt: string; // or Date if you convert it
  date: string;
  products: {
    [key: string]: DailyInventoryProduct;
  };
}

export interface DailyInventoryProduct {
  brand: string;
  category: string;
  discountAmount: number;
  discountPercentage: number;
  key: string;
  minimumQuantity: number;
  mrp: number;
  mrpTotalCost: number;
  productTax: string;
  purchaseStatus: string;
  productWeight: string;
  productName: string; // You have both productName & productname
  sku: number,
  quantity: number,
  unitPrice: number,
  totalCost: number
}

export interface InventoryTableRow {
  id: string;
  centreCode: string;
  date: string;
  productKey: string;
  brand: string;
  category: string;
  productName: string;
  quantity: number;
  mrp: number;
  mrpTotalCost: number;
}


@Component({
  selector: 'app-daily-inventory-count-report',
  templateUrl: './daily-inventory-count-report.component.html',
  styleUrls: ['./daily-inventory-count-report.component.scss']
})
export class DailyInventoryCountReportComponent implements OnInit, AfterViewInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  env = isDevMode() ? environment.testMode : environment.productionMode;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  inventoryList: any[] = [];
  dataSource = new MatTableDataSource<any>();
  selectedCentres: string[] = [];
  allCentreList: string[] = [];
  startDate!: Date | null;
  endDate!: Date | null;
  centres!: Observable<Centre[]>;
  baseColumns: string[] = [
    'sr',
    'date',
    'productName',
    'quantity',
    'openingStock',
    'closingStock',
  ];
  displayColumns: string[] = [];
  isLoading: boolean = false;
  noDataFound: boolean = false;

  constructor(private mFirestore: AngularFirestore,
              private centerService: CentreService,
              private readonly financialYearService: FinancialYearService,) {
    this.centres = this.centerService.getAllCentres()
  }

  ngOnInit(): void {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
    this.centres.pipe(takeUntil(this._destroyed$)).subscribe(list => {
      this.allCentreList = list.map(c => c.centreCode);
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onRangeChange(event: any) {
    if (this.startDate && this.endDate) {
      console.log("Start:", this.startDate);
      console.log("End:", this.endDate);
    }
  }

  formatDateForQuery(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${d}-${m}-${y}`;
  }

  getPreviousDate(date: Date): string {
    const prev = new Date(date);
    prev.setDate(prev.getDate() - 1);
    return this.formatDateForQuery(prev);
  }

  async getOpeningStockData(centre: string, startDate: Date) {

    const snap: any = await this.mFirestore.collection(
      this.env.dailyInventoryCount,
      ref => ref.where("centreCode", "==", centre)
    ).get().toPromise();

    if (snap.empty) return {};

    // Filter out docs without valid date
    const docs = snap.docs
      .filter((x: any) => x.data().date)       // ⬅ SKIP if date is missing
      .sort((a: any, b: any) => {
        const [da, ma, ya] = a.data().date.split('-').map(Number);
        const [db, mb, yb] = b.data().date.split('-').map(Number);

        return new Date(ya, ma - 1, da).getTime() -
          new Date(yb, mb - 1, db).getTime();
      });

    // If all docs had no date
    if (docs.length === 0) return {};

    const history: any = {};

    docs.forEach((doc: any) => {
      const data = doc.data();
      const [d, m, y] = data.date.split('-').map(Number);
      const docDate = new Date(y, m - 1, d);

      Object.keys(data.products).forEach(key => {
        if (!history[key]) history[key] = [];
        history[key].push({
          date: docDate,
          qty: data.products[key].quantity
        });
      });
    });

    const opening: any = {};

    Object.keys(history).forEach(key => {
      const list = history[key];

      let best = null;
      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].date <= startDate) {
          best = list[i];
          break;
        }
      }

      if (!best) best = list[0];
      opening[key] = { quantity: best.qty };
    });

    return opening;
  }





  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  async getClosingStockData(centre: string, endDate: Date) {

    const snap: any = await this.mFirestore.collection(
      this.env.dailyInventoryCount,
      ref => ref.where("centreCode", "==", centre)
    ).get().toPromise();

    if (snap.empty) return {};

    const docs = snap.docs
      .filter((x: any) => x.data().date)     // ⬅ SKIP docs without date
      .sort((a: any, b: any) => {
        const [da, ma, ya] = a.data().date.split('-').map(Number);
        const [db, mb, yb] = b.data().date.split('-').map(Number);

        return new Date(ya, ma - 1, da).getTime() -
          new Date(yb, mb - 1, db).getTime();
      });

    if (docs.length === 0) return {};

    let finalDoc = null;

    for (let i = docs.length - 1; i >= 0; i--) {
      const data = docs[i].data();
      const [d, m, y] = data.date.split('-').map(Number);
      const docDate = new Date(y, m - 1, d);

      if (docDate <= endDate) {
        finalDoc = data;
        break;
      }
    }

    if (!finalDoc) return {};

    return finalDoc.products || {};
  }



  updateDisplayColumns() {
    if (this.selectedCentres.length > 1) {
      this.displayColumns = ['sr', 'centreCode', 'date', 'productName', 'quantity', 'openingStock', 'closingStock'];
    } else {
      this.displayColumns = [...this.baseColumns];
    }
  }

  applyFilter(event: any) {
    this.dataSource.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
  }

  onSearch() {
    this.mSearch();
  }

  onClear() {
    this.selectedCentres = [];
  }

  async mSearch() {
    if (this.selectedCentres.length === 0) {
      alert("Please select first center.")
      return;
    }

    if (!this.startDate || !this.endDate) {
      alert("Please select a date range first.");
      return;
    }

    this.isLoading = true;
    this.noDataFound = true;

    const startD = new Date(this.startDate!);
    const endD = new Date(this.endDate!);
    const rows: any[] = [];

    for (const centre of this.selectedCentres) {
      let ref = this.mFirestore.collection(this.env.dailyInventoryCount).ref
        .where("centreCode", "==", centre)
        .orderBy("date");

      const snapshot = await ref.get();

      const filteredDocs = snapshot.docs.filter((doc: any) => {
        const data = doc.data();

        // Skip doc if date is missing
        if (!data.date) return false;

        const [d, m, y] = data.date.split('-').map(Number);
        const docDate = new Date(y, m - 1, d);

        return docDate >= startD && docDate <= endD;
      })

      const openingStockData = await this.getOpeningStockData(centre, startD);
      const closingStockData = await this.getClosingStockData(centre, endD);

      // GROUP BY PRODUCT - Track first and last appearance
      const productMap = new Map<string, any>();

      // First, collect all product appearances sorted by date
      const productAppearances = new Map<string, any[]>();

      filteredDocs.forEach(doc => {
        const data = doc.data() as DailyInventoryCount;
        const [d, m, y] = data.date.split('-').map(Number);
        const docDate = new Date(y, m - 1, d);

        Object.keys(data.products).forEach(key => {
          const p = data.products[key];

          if (!productAppearances.has(key)) {
            productAppearances.set(key, []);
          }

          productAppearances.get(key)!.push({
            doc: doc,
            data: data,
            docDate: docDate,
            product: p
          });
        });
      });

      //  Now process each product
      productAppearances.forEach((appearances, key) => {
        // Sort by date
        appearances.sort((a, b) => a.docDate.getTime() - b.docDate.getTime());

        const firstAppearance = appearances[0];
        const lastAppearance = appearances[appearances.length - 1];

        const p = lastAppearance.product;

        // Check if product exists in opening stock with valid (non-zero) quantity
        let openingQty: number;

        const hasValidOpeningStock = key in openingStockData &&
          openingStockData[key]?.quantity !== undefined &&
          openingStockData[key]?.quantity > 0;

        if (hasValidOpeningStock) {
          openingQty = openingStockData[key].quantity;
        } else {
          openingQty = firstAppearance.product.quantity;
        }

        const closingQty = closingStockData[key]?.quantity || lastAppearance.product.quantity;
        // alert(lastAppearance)
        productMap.set(key, {
          id: lastAppearance.doc.id,
          centreCode: lastAppearance.data.centreCode,
          date: lastAppearance.data.date,
          productKey: key,
          productName: p.productName,
          quantity: p.quantity,
          openingStock: openingQty,
          closingStock: closingQty
        });
      });

      // Convert map to array
      productMap.forEach(item => {
        rows.push(item);
      });
    }
    this.updateDisplayColumns();
    this.dataSource.data = rows;
    this.noDataFound = rows.length === 0;
    this.isLoading = false;
  }

  clearAll() {
    this.selectedCentres = [];
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
    this.inventoryList = [];
    this.dataSource.data = [];
    setTimeout(() => {
      if (this.paginator) this.paginator.firstPage();
      this.dataSource.paginator = this.paginator;
    });
  }

}

